import { useRegisterSW } from 'virtual:pwa-register/vue'
import { useToast } from '~/composables/useToast'
import { parseVersionStamp, isNewerVersion, type VersionStamp } from '~/utils/pwaVersion'
import { hardResetAndReload } from '~/utils/swReset'

export default defineNuxtPlugin(() => {
  if (import.meta.server) return

  const toast = useToast()
  let updateNotified = false

  // No auto-recargar: evita borrar trabajo sin guardar a media consulta,
  // formulario de admin, o cualquier flujo con datos no confirmados.
  // En su lugar, notificar y dejar que el usuario recargue cuando sea seguro.
  function applyUpdate() {
    if (updateNotified) return
    updateNotified = true
    let toastId: string
    toastId = toast.info('Nueva versión disponible.', 0, {
      label: 'Recargar ahora',
      onClick: () => {
        toast.remove(toastId)
        toast.info('Actualizando…', 0)
        void hardReset()
      },
    })
  }

  // "Recargar ahora" hace un HARD reset, no un reload pelón: desregistra el SW +
  // purga el precache para que UN solo click escape de un SW viejo atorado. Un
  // reload normal NO desregistra el SW → el toast volvía tras recargar (incidente
  // recurrente Dra. Sonia; ver G-263). Sigue siendo acción MANUAL — no viola la
  // regla "nunca auto-reload": sólo cambia lo que el click ya pedido ejecuta.
  // Best-effort con tope de 1.5s: si la limpieza se cuelga, recargamos igual para
  // no atrapar al usuario en un toast muerto. NO toca localStorage (no desloguea).
  // Compartido con `error.vue` (G-272): los dos botones que prometen "recargar"
  // tienen que escapar del mismo SW atorado, así que ejecutan el mismo código.
  const hardReset = hardResetAndReload

  // ── Version polling (primary) ─────────────────────────────────────────────
  // Polls /version.json (server/routes/version.json.ts), prerendered per build;
  // su `id` cambia en cada deploy → un build nuevo está vivo → notificar.
  //
  // El bug original: el poll comparaba /_nuxt/builds/latest.json y disparaba
  // `applyUpdate()` ante CUALQUIER diferencia (A→B y también B→A). El edge de
  // Netlify cachea los estáticos y los purga por deploy; durante la ventana de
  // propagación distintos PoPs sirven valores distintos → el poll veía el valor
  // ir y volver → toast en loop a través de recargas (inmune a cmd+shift+R, la
  // staleza es del edge, no del browser).
  //
  // Doble defensa:
  //  1) `known` es marca de agua MONOTÓNICA: solo notifica si el id cambia Y el
  //     timestamp AVANZA. Un flap hacia un build viejo (t menor) se ignora → mata
  //     el loop sin importar la caché del edge. Ver utils/pwaVersion.ts.
  //  2) /version.json se sirve sin caché en el EDGE vía `Netlify-CDN-Cache-Control:
  //     no-store` (netlify.toml) — `Cache-Control` solo no basta (controla el
  //     browser, no el edge). Así no hay flap de propagación de origen.
  let known: VersionStamp | null = null

  async function checkVersion() {
    try {
      const res = await fetch(`/version.json?t=${Date.now()}`, { cache: 'no-store' })
      if (!res.ok) return
      const data = parseVersionStamp(await res.json().catch(() => null))
      if (!data) return
      if (known === null) {
        known = data
      } else if (isNewerVersion(known, data)) {
        known = data
        applyUpdate()
      }
    } catch {}
  }

  checkVersion()
  setInterval(checkVersion, 3 * 60 * 1000) // every 3 min
  document.addEventListener('visibilitychange', () => {
    if (document.visibilityState === 'visible') checkVersion()
  })

  // ── SW update detection (secondary) ──────────────────────────────────────
  // Guard: controllerchange also fires when the SW claims the page for the
  // first time (initial activation via clients.claim()). Only show the prompt
  // when there was already a controller before — i.e. a genuine SW swap.
  const hadControllerOnLoad = !!navigator.serviceWorker.controller

  useRegisterSW({
    onRegisteredSW(_url, reg) {
      if (!reg) return
      reg.addEventListener('updatefound', () => {
        // Guard: en un hard-reload (cmd+shift+R) la página carga SIN controller,
        // pero el SW igual se actualiza → este path disparaba el toast aunque el
        // usuario acaba de recargar. Suprimir cuando no había controller al cargar
        // (mismo criterio que controllerchange abajo).
        if (!hadControllerOnLoad) return
        // Guard: updatefound fires on first install too. Only treat it as an
        // update if an active SW was already running.
        if (!reg.active) return
        const worker = reg.installing
        if (!worker) return
        worker.addEventListener('statechange', () => {
          if (worker.state === 'activated') applyUpdate()
        })
      })
    },
  })

  navigator.serviceWorker.addEventListener('controllerchange', () => {
    if (!hadControllerOnLoad) return
    applyUpdate()
  })
})
